import { useEffect, useRef, useState } from 'react';
import {
  batch, shallowEqual, useDispatch, useSelector,
} from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { useAuth } from 'utils/hooks/useAuth';
import { POPUP } from 'utils/constants/popup';
import { ROUTE_NAME, ROUTE_PATH } from 'utils/constants/router';
import { OTHER_DISEASE } from 'utils/constants/disease';
import { SUCCESS } from 'utils/constants/queryStatuses';
import { setPopup } from 'modules/popup/actions';
import { useKeyValueStore } from 'modules/keyValueStore/useKeyValueStore';
import { getUserCondition, getUserWixId } from 'modules/user/utils';
import { FINISHED_SIGNUP_AT_KEY } from 'utils/constants/storeGlobalKey';
import { useUpdateEffect } from 'utils/hooks/useUpdateEffect';

const CALL_HISTORY_STORE_KEY = 'flow_call_history';

const CHECK_INTERVAL = 5 * 1000;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const INTAKE_ONBOARDING_FLOW_DALEY = 15 * 1000; // 15 sec
const POPUP_COOL_DOWN = 3 * 1000; // 3 sec

const INTAKE_ONBOARDING_FLOW = 'intake_onboarding';

const since = (date) => Date.now() - new Date(date).getTime();

const getNextFlow = ({
  isAuthenticated,
  pathname,
  appConfiguration,
  profile,
  popup,
  callHistory = [],
  keyValueStore,
  authenticatedAt,
  popupClosedAt,
}) => {
  const { status: appConfigurationStatus } = appConfiguration;

  const flowGetters = [
    () => {
      if (!authenticatedAt || since(authenticatedAt) < INTAKE_ONBOARDING_FLOW_DALEY) {
        return null;
      }

      // help handle logout
      const flowId = `${INTAKE_ONBOARDING_FLOW}_${getUserWixId(profile)}`;

      if (
        appConfigurationStatus !== SUCCESS
        || !isAuthenticated
        || popup
        || callHistory.find(({ id }) => id === flowId)
        || getUserCondition(profile) === OTHER_DISEASE
      ) {
        return null;
      }
      const { userConfiguration: { isIntakeOnboardingShown } = {} } = appConfiguration;
      if (isIntakeOnboardingShown) {
        return null;
      }
      const { personal: { profile_created_at: profileCreatedAt } } = profile;
      if (since(profileCreatedAt) > DAY_IN_MS) {
        // safeguard for an old profile without isIntakeOnboardingShown set
        return null;
      }

      const { [FINISHED_SIGNUP_AT_KEY]: finishedSignupAt } = keyValueStore;
      if (finishedSignupAt && since(finishedSignupAt) < INTAKE_ONBOARDING_FLOW_DALEY) {
        return null;
      }

      if (popupClosedAt && since(popupClosedAt) < POPUP_COOL_DOWN) {
        return null;
      }

      const isTargetPath = [ROUTE_PATH[ROUTE_NAME.RESULTS], ROUTE_PATH[ROUTE_NAME.FEED]].some((path) => matchPath(pathname, {
        path,
        exact: true,
      }));
      if (!isTargetPath) {
        return null;
      }
      return {
        id: flowId,
        action: setPopup({
          type: POPUP.INTAKE_ONBOARDING,
          data: {
            theme: 'middle', autoWidth: true, skipClose: true, isFullScreen: false, preventOverlayClose: true,
          },
        }),
      };
    },
  ];
  for (const getNextAction of flowGetters) {
    const nextAction = getNextAction();
    if (nextAction) {
      return nextAction;
    }
  }
  return null;
};

export const useAppFlow = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();

  const [count, setCount] = useState(0);

  const {
    appConfiguration,
    profile: { data: profile },
    popup: { popup },
    keyValueStore,
  } = useSelector((state) => ({
    appConfiguration: state.appConfiguration,
    profile: state.profile,
    popup: state.popup,
    keyValueStore: state.keyValueStore,
  }), shallowEqual);

  const [callHistory, setCallHistory] = useKeyValueStore(CALL_HISTORY_STORE_KEY);

  const authenticatedAtRef = useRef(isAuthenticated ? Date.now() : null);
  const popupClosedAtRef = useRef(null);

  useUpdateEffect(() => {
    authenticatedAtRef.current = isAuthenticated ? Date.now() : null;
  }, [isAuthenticated]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCount((v) => v + 1);
    }, CHECK_INTERVAL);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useUpdateEffect(() => {
    popupClosedAtRef.current = popup ? null : Date.now();
  }, [popup]);

  useEffect(() => {
    const nextAction = getNextFlow({
      isAuthenticated,
      pathname,
      appConfiguration,
      profile,
      popup,
      callHistory,
      keyValueStore,
      authenticatedAt: authenticatedAtRef.current,
      popupClosedAt: popupClosedAtRef.current,
    });
    if (nextAction) {
      const { action: actionToDispatch, id: flowId } = nextAction;
      batch(() => {
        dispatch(actionToDispatch);
        setCallHistory((currentCallHistory) => [...(currentCallHistory ?? []), { id: flowId, timestamp: Date.now() }]);
      });
    }
  }, [
    count,
    appConfiguration,
    dispatch,
    isAuthenticated,
    profile,
    pathname,
    popup,
    callHistory,
    setCallHistory,
    keyValueStore,
  ]);

  // TODO TBD add clearing of flow_call_history store on logout
};
